import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DayjsWithTZ } from '@/utils/dayjs';
import type { TimeDisplayFont } from './fontOptions';
import { getFontOption } from './fontOptions';
import SevenSegDigit from './SevenSegDigit';
import { useSevenSegConfig } from './useSevenSegConfig';

const largeFontSize = '2em';

interface DateDisplayProps {
    now: DayjsWithTZ;
    font?: TimeDisplayFont;
    fontSize?: number; // レスポンシブフォントサイズ
}

export const DateDisplay: React.FC<DateDisplayProps> = ({
    now,
    font = 'normal',
    fontSize: responsiveFontSize,
}) => {
    const { t, i18n } = useTranslation('timePanel');
    const fontOption = getFontOption(font);
    const fontFamily = fontOption.fontFamily;
    const isDigital7 =
        font === 'digital-7' ||
        font === 'digital-7-dark' ||
        font === 'digital-7-max' ||
        font === 'digital-7-max-dark';
    const isMax = font === 'digital-7-max' || font === 'digital-7-max-dark';
    const isDark = fontOption.isDark === true;

    const weekdaysRaw = t('date.weekdays', { returnObjects: true });
    const weekdays = Array.isArray(weekdaysRaw)
        ? (weekdaysRaw as string[])
        : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const monthNum = now.month() + 1;
    const dayNum = now.date();
    const weekdayValue = weekdays[now.day()] ?? '';

    const isEnglish = i18n.language?.toLowerCase().startsWith('en');
    const isJapanese = i18n.language?.toLowerCase().startsWith('ja');

    // 7セグメント表示の設定
    const segConfig = useSevenSegConfig(isDark);
    // レスポンシブ対応: fontSizeに基づいてサイズを計算
    // MAX版の場合はサイズを大きくする
    const baseFontSize = responsiveFontSize || 16;
    const scaleFactor = isMax ? 1 : 1;
    const {
        width: segWidth,
        height: segHeight,
        thickness: segThickness,
    } = segConfig.getDateDisplaySize(baseFontSize * scaleFactor);

    // デジタルMAX用：日付も横幅に応じてスケールさせる
    const containerRef = useRef<HTMLDivElement | null>(null);
    const rowRef = useRef<HTMLDivElement | null>(null);
    const [maxScale, setMaxScale] = useState(1);

    useEffect(() => {
        if (!isDigital7 || !isMax) {
            setMaxScale(1);
            return;
        }

        const container = containerRef.current;
        const row = rowRef.current;
        if (!container || !row) return;

        const calcScale = () => {
            const containerWidth = container.getBoundingClientRect().width;
            const contentWidth = row.scrollWidth;
            if (!containerWidth || !contentWidth) return;
            const scale = (containerWidth * 0.5) / contentWidth;
            setMaxScale(Math.max(0.7, Math.min(2, scale)));
        };

        const observer = new ResizeObserver(() => {
            calcScale();
        });

        observer.observe(container);
        calcScale();

        return () => {
            observer.disconnect();
        };
        // 数値の桁が変わると幅も変わるので、そのタイミングで再計算
    }, [isDigital7, isMax, monthNum, dayNum, weekdayValue]);

    // 数字を1文字ずつに分割して固定幅で表示する関数
    const renderDigits = (value: number, fontSize: string) => {
        const valueStr = value.toString();
        if (!isDigital7) {
            return (
                <span style={{ fontSize, ...(fontFamily ? { fontFamily } : {}) }}>{valueStr}</span>
            );
        }

        // 7セグメント表示（1桁の場合は先頭に空白を追加して2桁表示）
        const displayStr = valueStr.padStart(2, ' ');
        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3em',
                    width: `${segWidth * 2 + segWidth * 0.3}px`,
                    height: `${segHeight}px`,
                }}
                className="max-w-full"
            >
                {displayStr.split('').map((digit, index) => (
                    <div
                        key={index}
                        style={{
                            width: `${segWidth}px`,
                            height: `${segHeight}px`,
                            flexShrink: 0,
                        }}
                        className="max-w-full max-h-full"
                    >
                        <SevenSegDigit
                            value={digit}
                            width={segWidth}
                            height={segHeight}
                            thickness={segThickness}
                            on={segConfig.segOnColor}
                            off={segConfig.segOffColor}
                        />
                    </div>
                ))}
            </span>
        );
    };

    if (isEnglish) {
        return (
            // テキストは下揃え、month / day (weekday)
            <div
                ref={containerRef}
                className="w-full flex justify-center items-baseline select-none"
            >
                <div
                    ref={rowRef}
                    className="flex justify-center items-baseline"
                    style={
                        isMax
                            ? {
                                  transform: `scale(${maxScale})`,
                                  transformOrigin: 'center bottom',
                              }
                            : undefined
                    }
                >
                    <span
                        style={{
                            color: isDark ? '#ffffff' : undefined,
                            backgroundColor: isDark && isDigital7 ? '#000000' : undefined,
                            padding: isDark && isDigital7 ? '0.1em 0.2em' : undefined,
                            borderRadius: isDark && isDigital7 ? '0.1em' : undefined,
                        }}
                    >
                        {renderDigits(monthNum, largeFontSize)}
                    </span>
                    <span
                        className={isDark ? '' : 'text-gray-500'}
                        style={{
                            fontSize: '1.5em',
                            fontFamily: isDigital7 ? fontFamily : undefined,
                            color: isDark ? '#ffffff' : undefined,
                        }}
                    >
                        /
                    </span>
                    <span
                        style={{
                            color: isDark ? '#ffffff' : undefined,
                            backgroundColor: isDark && isDigital7 ? '#000000' : undefined,
                            padding: isDark && isDigital7 ? '0.1em 0.2em' : undefined,
                            borderRadius: isDark && isDigital7 ? '0.1em' : undefined,
                        }}
                    >
                        {renderDigits(dayNum, largeFontSize)}
                    </span>
                    <span
                        className={isDark ? '' : 'text-gray-500'}
                        style={{
                            fontSize: '1.5em',
                            margin: '0 0.2em',
                            fontFamily,
                            color: isDark ? '#ffffff' : undefined,
                        }}
                    >
                        {` (${weekdayValue})`}
                    </span>
                </div>
            </div>
        );
    }

    if (isJapanese) {
        // 日本語は「n月 n日（曜）」の固定表記
        // 数字部分のみDigital-7フォントを適用、日本語文字はデフォルトフォント
        return (
            <div
                ref={containerRef}
                className="w-full flex justify-center items-baseline select-none"
            >
                <div
                    ref={rowRef}
                    className="flex justify-center items-baseline"
                    style={
                        isMax
                            ? {
                                  transform: `scale(${maxScale})`,
                                  transformOrigin: 'center bottom',
                              }
                            : undefined
                    }
                >
                    <span
                        style={{
                            color: isDark ? '#ffffff' : undefined,
                            backgroundColor: isDark && isDigital7 ? '#000000' : undefined,
                            padding: isDark && isDigital7 ? '0.1em 0.2em' : undefined,
                            borderRadius: isDark && isDigital7 ? '0.1em' : undefined,
                        }}
                    >
                        {renderDigits(monthNum, largeFontSize)}
                    </span>
                    <span
                        className={isDark ? '' : 'text-gray-500'}
                        style={{
                            margin: '0 0.2em',
                            color: isDark ? '#ffffff' : undefined,
                        }}
                    >
                        月
                    </span>
                    <span
                        style={{
                            color: isDark ? '#ffffff' : undefined,
                            backgroundColor: isDark && isDigital7 ? '#000000' : undefined,
                            padding: isDark && isDigital7 ? '0.1em 0.2em' : undefined,
                            borderRadius: isDark && isDigital7 ? '0.1em' : undefined,
                        }}
                    >
                        {renderDigits(dayNum, largeFontSize)}
                    </span>
                    <span
                        className={isDark ? '' : 'text-gray-500'}
                        style={{
                            margin: '0 0.2em',
                            color: isDark ? '#ffffff' : undefined,
                        }}
                    >
                        日
                    </span>
                    <span
                        className={isDark ? '' : 'text-gray-500'}
                        style={{
                            fontSize: '1.4em',
                            color: isDark ? '#ffffff' : undefined,
                        }}
                    >
                        （{weekdayValue}）
                    </span>
                </div>
            </div>
        );
    }

    // その他の言語は英語スタイルに合わせる
    return (
        <div
            ref={containerRef}
            className="w-full flex justify-center items-baseline select-none"
        >
            <div
                ref={rowRef}
                className="flex justify-center items-baseline"
                style={
                    isMax
                        ? {
                              transform: `scale(${maxScale})`,
                              transformOrigin: 'center bottom',
                          }
                        : undefined
                }
            >
                <span
                    style={{
                        color: isDark ? '#ffffff' : undefined,
                        backgroundColor: isDark && isDigital7 ? '#000000' : undefined,
                        padding: isDark && isDigital7 ? '0.1em 0.2em' : undefined,
                        borderRadius: isDark && isDigital7 ? '0.1em' : undefined,
                    }}
                >
                    {renderDigits(monthNum, largeFontSize)}
                </span>
                <span
                    className={isDark ? '' : 'text-gray-500'}
                    style={{
                        fontSize: '1.5em',
                        fontFamily: isDigital7 ? fontFamily : undefined,
                        color: isDark ? '#ffffff' : undefined,
                    }}
                >
                    /
                </span>
                <span
                    style={{
                        color: isDark ? '#ffffff' : undefined,
                        backgroundColor: isDark && isDigital7 ? '#000000' : undefined,
                        padding: isDark && isDigital7 ? '0.1em 0.2em' : undefined,
                        borderRadius: isDark && isDigital7 ? '0.1em' : undefined,
                    }}
                >
                    {renderDigits(dayNum, largeFontSize)}
                </span>
                <span
                    className={isDark ? '' : 'text-gray-500'}
                    style={{
                        fontSize: '2.4em',
                        margin: '0 0.2em',
                        fontFamily,
                        color: isDark ? '#ffffff' : undefined,
                    }}
                >
                    {`(${weekdayValue})`}
                </span>
            </div>
        </div>
    );
};
